// 관리자 운영설정 페이지.
// 작업계획서 5.7 + Phase 3-B 결정사항:
//  - 요일별 영업시간 7행 (휴무 토글)
//  - 임시 휴무일 (YYYY-MM-DD 배열, 입력/제거)
//  - 주문 가능 시간 (영업시간과 별개, 둘 다 비우면 영업시간을 따름)
//  - 배달 운영 토글 + 키워드 + 최소금액 + 배달비
//  - 예약 주문 정책 (활성 토글, 최대 일수, 최소 사전 분, 슬롯 단위)
//  - 사용자 안내 문구
//
// PUT 전체 갱신 방식. 화면 진입 시 GET 으로 채우고, 저장 시 전체를 보낸다.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RESERVATION_SLOT_MINUTES_OPTIONS,
  WEEKDAYS_ORDERED,
  WEEKDAY_LABEL_KO,
  storeSettingsInputSchema,
  type BusinessHour,
  type ReservationSlotMinutes,
  COUPON_TYPE,
  type CouponSpec,
  type CouponType,
  type StoreSettingsDto,
  type StoreSettingsInput,
  type TierRule,
  type Weekday,
  type WeeklyHours,
} from "@oo/shared";
import { PageHeader } from "@/shared/ui";
import ImageUploader from "@/features/catalog/ImageUploader.tsx";
import { getStoreSettings, putStoreSettings } from "@/features/operations/storeSettingsApi.ts";

// 화면 상태: DTO 의 weekly_hours 를 UI 친화적인 7행 배열로 옮긴 형태.
interface WeekdayRow {
  weekday: Weekday;
  enabled: boolean;
  open: string;
  close: string;
}

function dtoToWeekdayRows(weekly: WeeklyHours): WeekdayRow[] {
  return WEEKDAYS_ORDERED.map((wd) => {
    const v = weekly[wd];
    return {
      weekday: wd,
      enabled: v !== null,
      open: v?.open ?? "09:00",
      close: v?.close ?? "21:00",
    };
  });
}

function weekdayRowsToDto(rows: WeekdayRow[]): WeeklyHours {
  const next: WeeklyHours = {
    mon: null,
    tue: null,
    wed: null,
    thu: null,
    fri: null,
    sat: null,
    sun: null,
  };
  for (const r of rows) {
    if (!r.enabled) continue;
    const v: BusinessHour = { open: r.open, close: r.close };
    next[r.weekday] = v;
  }
  return next;
}

// 매장 운영설정 편집 화면 (영업시간/배달/적립·쿠폰 정책).
export default function StoreSettingsPage() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "store-settings"],
    queryFn: getStoreSettings,
  });

  return (
    <div className="mx-auto w-full min-w-0 max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
      <div className="mb-4">
        <PageHeader
          title="운영 설정"
          description="영업시간/배달/적립·쿠폰 등 매장 운영 정책을 설정합니다."
          actions={
            query.data ? (
              <span className="text-xs text-ink-600">
                마지막 저장: {new Date(query.data.updatedAt).toLocaleString("ko-KR")}
              </span>
            ) : undefined
          }
        />
      </div>
      {query.isLoading ? (
        <p className="text-sm text-ink-600">불러오는 중...</p>
      ) : query.isError || !query.data ? (
        <p className="text-sm text-danger-600">불러오기에 실패했습니다.</p>
      ) : (
        <StoreSettingsForm
          initial={query.data}
          onSaved={(s) => qc.setQueryData(["admin", "store-settings"], s)}
        />
      )}
    </div>
  );
}

function StoreSettingsForm({
  initial,
  onSaved,
}: {
  initial: StoreSettingsDto;
  onSaved: (settings: StoreSettingsDto) => void;
}) {
  const [rows, setRows] = useState<WeekdayRow[]>(() => dtoToWeekdayRows(initial.weeklyHours));
  const [closedDates, setClosedDates] = useState<string[]>(initial.closedDates);
  const [closedDateInput, setClosedDateInput] = useState<string>("");
  const [orderWindowOpen, setOrderWindowOpen] = useState<string>(initial.orderWindowOpen ?? "");
  const [orderWindowClose, setOrderWindowClose] = useState<string>(initial.orderWindowClose ?? "");
  const [deliveryEnabled, setDeliveryEnabled] = useState(initial.deliveryEnabled);
  // 배달 주문 가능 시간(개선 2). 둘 다 비우면 제한 없음.
  const [deliveryWindowOpen, setDeliveryWindowOpen] = useState<string>(
    initial.deliveryWindowOpen ?? "",
  );
  const [deliveryWindowClose, setDeliveryWindowClose] = useState<string>(
    initial.deliveryWindowClose ?? "",
  );
  // 다음날 배달 전용. 켜면 배달 주문 가능 시간 입력이 비활성된다(최소금액/지역키워드는 유지).
  const [nextDayDeliveryOnly, setNextDayDeliveryOnly] = useState(initial.nextDayDeliveryOnly);
  const [keywords, setKeywords] = useState<string[]>(initial.deliveryAreaKeywords);
  const [keywordInput, setKeywordInput] = useState("");
  const [minOrderKrw, setMinOrderKrw] = useState<number>(initial.deliveryMinOrderKrw);
  const [feeKrw, setFeeKrw] = useState<number>(initial.deliveryFeeKrw);
  const [noticeText, setNoticeText] = useState<string>(initial.noticeText ?? "");
  const [reservationEnabled, setReservationEnabled] = useState(initial.reservationEnabled);
  const [reservationMaxDaysAhead, setReservationMaxDaysAhead] = useState<number>(
    initial.reservationMaxDaysAhead,
  );
  const [reservationMinLeadMinutes, setReservationMinLeadMinutes] = useState<number>(
    initial.reservationMinLeadMinutes,
  );
  const [reservationSlotMinutes, setReservationSlotMinutes] = useState<ReservationSlotMinutes>(
    initial.reservationSlotMinutes,
  );
  const [pointsEnabled, setPointsEnabled] = useState(initial.pointsEnabled);
  const [pointsEarnPercent, setPointsEarnPercent] = useState<number>(initial.pointsEarnPercent);
  const [pointsEarnPercentDelivery, setPointsEarnPercentDelivery] = useState<number>(
    initial.pointsEarnPercentDelivery,
  );
  const [pointsMinUseKrw, setPointsMinUseKrw] = useState<number>(initial.pointsMinUseKrw);
  const [pointsUseUnitKrw, setPointsUseUnitKrw] = useState<number>(initial.pointsUseUnitKrw);
  const [tierEnabled, setTierEnabled] = useState(initial.tierEnabled);
  const [tiers, setTiers] = useState<TierRule[]>(initial.tiers);
  const [tierWindowMonths, setTierWindowMonths] = useState<number>(initial.tierWindowMonths);
  const [signupCoupons, setSignupCoupons] = useState<CouponSpec[]>(initial.signupCoupons);
  const [contactPhone, setContactPhone] = useState<string>(initial.contactPhone ?? "");
  const [kakaoUrl, setKakaoUrl] = useState<string>(initial.kakaoUrl ?? "");
  const [splashImageKey, setSplashImageKey] = useState<string | null>(initial.splashImageKey);
  // 칭찬하고 할인받기(개선 5~8).
  const [complimentEnabled, setComplimentEnabled] = useState(initial.complimentEnabled);
  const [complimentPickupCount, setComplimentPickupCount] = useState<number>(
    initial.complimentPickupCount,
  );
  const [complimentDeliveryCount, setComplimentDeliveryCount] = useState<number>(
    initial.complimentDeliveryCount,
  );
  const [complimentDiscountMinKrw, setComplimentDiscountMinKrw] = useState<number>(
    initial.complimentDiscountMinKrw,
  );
  const [complimentDiscountMaxKrw, setComplimentDiscountMaxKrw] = useState<number>(
    initial.complimentDiscountMaxKrw,
  );
  const [complimentDiscountUnitKrw, setComplimentDiscountUnitKrw] = useState<number>(
    initial.complimentDiscountUnitKrw,
  );
  const [complimentList, setComplimentList] = useState<string[]>(initial.complimentList);
  const [complimentModalOpen, setComplimentModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // initial 이 react-query 캐시로 갱신될 때 폼 상태를 동기화.
  useEffect(() => {
    setRows(dtoToWeekdayRows(initial.weeklyHours));
    setClosedDates(initial.closedDates);
    setOrderWindowOpen(initial.orderWindowOpen ?? "");
    setOrderWindowClose(initial.orderWindowClose ?? "");
    setDeliveryEnabled(initial.deliveryEnabled);
    setDeliveryWindowOpen(initial.deliveryWindowOpen ?? "");
    setDeliveryWindowClose(initial.deliveryWindowClose ?? "");
    setNextDayDeliveryOnly(initial.nextDayDeliveryOnly);
    setKeywords(initial.deliveryAreaKeywords);
    setMinOrderKrw(initial.deliveryMinOrderKrw);
    setFeeKrw(initial.deliveryFeeKrw);
    setNoticeText(initial.noticeText ?? "");
    setReservationEnabled(initial.reservationEnabled);
    setReservationMaxDaysAhead(initial.reservationMaxDaysAhead);
    setReservationMinLeadMinutes(initial.reservationMinLeadMinutes);
    setReservationSlotMinutes(initial.reservationSlotMinutes);
    setPointsEnabled(initial.pointsEnabled);
    setPointsEarnPercent(initial.pointsEarnPercent);
    setPointsEarnPercentDelivery(initial.pointsEarnPercentDelivery);
    setPointsMinUseKrw(initial.pointsMinUseKrw);
    setPointsUseUnitKrw(initial.pointsUseUnitKrw);
    setTierEnabled(initial.tierEnabled);
    setTiers(initial.tiers);
    setTierWindowMonths(initial.tierWindowMonths);
    setSignupCoupons(initial.signupCoupons);
    setContactPhone(initial.contactPhone ?? "");
    setKakaoUrl(initial.kakaoUrl ?? "");
    setSplashImageKey(initial.splashImageKey);
    setComplimentEnabled(initial.complimentEnabled);
    setComplimentPickupCount(initial.complimentPickupCount);
    setComplimentDeliveryCount(initial.complimentDeliveryCount);
    setComplimentDiscountMinKrw(initial.complimentDiscountMinKrw);
    setComplimentDiscountMaxKrw(initial.complimentDiscountMaxKrw);
    setComplimentDiscountUnitKrw(initial.complimentDiscountUnitKrw);
    setComplimentList(initial.complimentList);
  }, [initial]);

  const mutation = useMutation({
    mutationFn: putStoreSettings,
    onSuccess: (s) => {
      onSaved(s);
      setSuccess("운영 설정을 저장했습니다.");
      setError(null);
      window.setTimeout(() => setSuccess(null), 2500);
    },
    onError: (err: unknown) => {
      setSuccess(null);
      setError(err instanceof Error ? err.message : "저장에 실패했습니다.");
    },
  });

  const payload = useMemo<StoreSettingsInput>(
    () => ({
      weeklyHours: weekdayRowsToDto(rows),
      closedDates,
      orderWindowOpen: orderWindowOpen.trim() ? orderWindowOpen : null,
      orderWindowClose: orderWindowClose.trim() ? orderWindowClose : null,
      deliveryEnabled,
      deliveryWindowOpen: deliveryWindowOpen.trim() ? deliveryWindowOpen : null,
      deliveryWindowClose: deliveryWindowClose.trim() ? deliveryWindowClose : null,
      deliveryAreaKeywords: keywords,
      deliveryMinOrderKrw: Math.max(0, Math.floor(minOrderKrw || 0)),
      deliveryFeeKrw: Math.max(0, Math.floor(feeKrw || 0)),
      nextDayDeliveryOnly,
      // 칭찬하고 할인받기(개선 5~8). 빈 문구 제거 + 음수/단위 정규화.
      complimentEnabled,
      complimentPickupCount: Math.max(0, Math.min(20, Math.floor(complimentPickupCount || 0))),
      complimentDeliveryCount: Math.max(0, Math.min(20, Math.floor(complimentDeliveryCount || 0))),
      complimentDiscountMinKrw: Math.max(0, Math.floor(complimentDiscountMinKrw || 0)),
      complimentDiscountMaxKrw: Math.max(0, Math.floor(complimentDiscountMaxKrw || 0)),
      complimentDiscountUnitKrw: Math.max(1, Math.floor(complimentDiscountUnitKrw || 1)),
      complimentList: complimentList.map((s) => s.trim()).filter((s) => s.length > 0),
      noticeText: noticeText.trim() ? noticeText : null,
      reservationEnabled,
      reservationMaxDaysAhead,
      reservationMinLeadMinutes,
      reservationSlotMinutes,
      pointsEnabled,
      pointsEarnPercent: Math.max(0, Math.min(100, Math.floor(pointsEarnPercent || 0))),
      pointsEarnPercentDelivery: Math.max(
        0,
        Math.min(100, Math.floor(pointsEarnPercentDelivery || 0)),
      ),
      // 적립금 사용 정책. 최소사용은 0 이상, 단위는 1 이상으로 정규화.
      pointsMinUseKrw: Math.max(0, Math.floor(pointsMinUseKrw || 0)),
      pointsUseUnitKrw: Math.max(1, Math.floor(pointsUseUnitKrw || 1)),
      tierEnabled,
      // 이름이 빈 행은 제출에서 제외하고, 금액은 음수 방지.
      tiers: tiers
        .filter((t) => t.name.trim().length > 0)
        .map((t) => ({
          key: t.key,
          name: t.name.trim(),
          minSpendKrw: Math.max(0, Math.floor(t.minSpendKrw || 0)),
          // 등급 월간 쿠폰팩 - 이름 빈 행 제외, free_delivery value 0 정규화.
          coupons: t.coupons.filter((c) => c.name.trim().length > 0).map(normalizeCouponSpec),
        })),
      tierWindowMonths: Math.max(1, Math.min(24, Math.floor(tierWindowMonths || 3))),
      // 이름이 빈 행은 제외. free_delivery 는 value 0 으로 정규화.
      signupCoupons: signupCoupons.filter((s) => s.name.trim().length > 0).map(normalizeCouponSpec),
      contactPhone: contactPhone.trim() ? contactPhone.trim() : null,
      kakaoUrl: kakaoUrl.trim() ? kakaoUrl.trim() : null,
      splashImageKey,
    }),
    [
      rows,
      closedDates,
      orderWindowOpen,
      orderWindowClose,
      deliveryEnabled,
      deliveryWindowOpen,
      deliveryWindowClose,
      nextDayDeliveryOnly,
      keywords,
      minOrderKrw,
      feeKrw,
      complimentEnabled,
      complimentPickupCount,
      complimentDeliveryCount,
      complimentDiscountMinKrw,
      complimentDiscountMaxKrw,
      complimentDiscountUnitKrw,
      complimentList,
      noticeText,
      reservationEnabled,
      reservationMaxDaysAhead,
      reservationMinLeadMinutes,
      reservationSlotMinutes,
      pointsEnabled,
      pointsEarnPercent,
      pointsEarnPercentDelivery,
      pointsMinUseKrw,
      pointsUseUnitKrw,
      tierEnabled,
      tiers,
      tierWindowMonths,
      signupCoupons,
      contactPhone,
      kakaoUrl,
      splashImageKey,
    ],
  );

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = storeSettingsInputSchema.safeParse(payload);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "입력값을 확인해 주세요.");
      setSuccess(null);
      return;
    }
    mutation.mutate(parsed.data);
  }

  function addClosedDate() {
    const v = closedDateInput.trim();
    if (!v) return;
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(v)) {
      setError("휴무일 형식은 YYYY-MM-DD 이어야 합니다.");
      return;
    }
    if (closedDates.includes(v)) return;
    setClosedDates([...closedDates, v].sort());
    setClosedDateInput("");
    setError(null);
  }

  function addKeyword() {
    const v = keywordInput.trim();
    if (!v) return;
    if (keywords.includes(v)) return;
    setKeywords([...keywords, v]);
    setKeywordInput("");
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex min-w-0 max-w-full flex-col gap-6 [&>details]:w-full [&>details]:min-w-0 [&>details]:max-w-full"
    >
      {/* 영업시간 */}
      <details className="w-full min-w-0 max-w-full rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          요일별 영업시간
        </summary>
        <p className="mb-3 text-xs text-ink-600">
          끄면 정기 휴무로 처리됩니다. 시작 시각은 마감 시각보다 빨라야 합니다.
        </p>
        <div className="flex flex-col gap-2">
          {rows.map((r) => (
            <div
              key={r.weekday}
              className="grid min-w-0 grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-x-2 gap-y-2 rounded-md bg-ink-50 p-2 sm:flex sm:bg-transparent sm:p-0"
            >
              <span className="text-sm font-semibold sm:w-10">{WEEKDAY_LABEL_KO[r.weekday]}</span>
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={r.enabled}
                  onChange={(e) =>
                    setRows((rs) =>
                      rs.map((it) =>
                        it.weekday === r.weekday ? { ...it, enabled: e.target.checked } : it,
                      ),
                    )
                  }
                />
                영업
              </label>
              <div className="col-span-2 grid min-w-0 grid-cols-1 items-center gap-2 sm:flex">
                <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-ink-600">
                  <span className="sm:sr-only">시작 시각</span>
                  <input
                    type="time"
                    value={r.open}
                    disabled={!r.enabled}
                    onChange={(e) =>
                      setRows((rs) =>
                        rs.map((it) =>
                          it.weekday === r.weekday ? { ...it, open: e.target.value } : it,
                        ),
                      )
                    }
                    className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900 disabled:bg-ink-100"
                  />
                </label>
                <span className="hidden text-ink-600 sm:inline">~</span>
                <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-ink-600">
                  <span className="sm:sr-only">마감 시각</span>
                  <input
                    type="time"
                    value={r.close}
                    disabled={!r.enabled}
                    onChange={(e) =>
                      setRows((rs) =>
                        rs.map((it) =>
                          it.weekday === r.weekday ? { ...it, close: e.target.value } : it,
                        ),
                      )
                    }
                    className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900 disabled:bg-ink-100"
                  />
                </label>
              </div>
            </div>
          ))}
        </div>
      </details>

      {/* 임시 휴무일 */}
      <details className="w-full min-w-0 max-w-full rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          임시 휴무일
        </summary>
        <div className="flex min-w-0 items-center gap-2">
          <input
            type="date"
            value={closedDateInput}
            onChange={(e) => setClosedDateInput(e.target.value)}
            className="min-w-0 rounded-md border border-ink-300 px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={addClosedDate}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
          >
            추가
          </button>
        </div>
        <ul className="mt-3 flex flex-wrap gap-2">
          {closedDates.length === 0 ? (
            <li className="text-xs text-ink-600">등록된 임시 휴무일이 없습니다.</li>
          ) : (
            closedDates.map((d) => (
              <li
                key={d}
                className="flex items-center gap-1 rounded-full border border-ink-300 bg-ink-50 px-3 py-1 text-sm"
              >
                <span className="tabular-nums">{d}</span>
                <button
                  type="button"
                  onClick={() => setClosedDates(closedDates.filter((x) => x !== d))}
                  className="rounded-full px-1 text-xs text-ink-600 hover:bg-ink-200"
                  aria-label="휴무일 삭제"
                >
                  x
                </button>
              </li>
            ))
          )}
        </ul>
      </details>

      {/* 주문 가능 시간 */}
      <details className="w-full min-w-0 max-w-full rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          주문 가능 시간 (선택)
        </summary>
        <p className="mb-3 text-xs text-ink-600">
          비워두면 위의 영업시간과 동일합니다. "영업 21시까지지만 주문은 20시까지" 같이 별도 마감을
          둘 때 입력하세요.
        </p>
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
          <input
            type="time"
            value={orderWindowOpen}
            onChange={(e) => setOrderWindowOpen(e.target.value)}
            className="min-w-0 rounded-md border border-ink-300 px-2 py-1 text-sm"
          />
          <span className="text-ink-600">~</span>
          <input
            type="time"
            value={orderWindowClose}
            onChange={(e) => setOrderWindowClose(e.target.value)}
            className="min-w-0 rounded-md border border-ink-300 px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={() => {
              setOrderWindowOpen("");
              setOrderWindowClose("");
            }}
            className="col-span-3 justify-self-end rounded-md border border-ink-300 px-3 py-1 text-xs text-ink-700 hover:bg-ink-100 sm:col-span-1"
          >
            비우기
          </button>
        </div>
      </details>

      {/* 예약 주문 정책 */}
      <details className="rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          예약 주문
        </summary>
        <p className="mb-3 text-xs text-ink-600">
          손님이 휴무/마감 이후의 일자/시간 슬롯을 선택해 예약 주문할 수 있습니다 (예: 내일 09시
          수령).
        </p>
        <label className="mb-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={reservationEnabled}
            onChange={(e) => setReservationEnabled(e.target.checked)}
          />
          예약 주문 허용
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-600">예약 가능 기간 (일)</span>
            <input
              type="number"
              min={0}
              max={60}
              value={reservationMaxDaysAhead}
              onChange={(e) => setReservationMaxDaysAhead(Number.parseInt(e.target.value, 10) || 0)}
              disabled={!reservationEnabled}
              className="rounded-md border border-ink-300 px-2 py-1 disabled:bg-ink-100"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-600">최소 사전 시간 (분)</span>
            <input
              type="number"
              min={0}
              max={1440}
              step={5}
              value={reservationMinLeadMinutes}
              onChange={(e) =>
                setReservationMinLeadMinutes(Number.parseInt(e.target.value, 10) || 0)
              }
              disabled={!reservationEnabled}
              className="rounded-md border border-ink-300 px-2 py-1 disabled:bg-ink-100"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-600">슬롯 단위 (분)</span>
            <select
              value={reservationSlotMinutes}
              onChange={(e) =>
                setReservationSlotMinutes(
                  Number.parseInt(e.target.value, 10) as ReservationSlotMinutes,
                )
              }
              disabled={!reservationEnabled}
              className="rounded-md border border-ink-300 px-2 py-1 disabled:bg-ink-100"
            >
              {RESERVATION_SLOT_MINUTES_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        </div>
      </details>

      {/* 적립금 정책 */}
      <details className="rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          적립금
        </summary>
        <p className="mb-3 text-xs text-ink-600">
          적립은 주문이 수령 완료될 때 확정됩니다. 적립률은 주문 합계 기준 퍼센트입니다 (0이면
          적립하지 않음).
        </p>
        <label className="mb-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={pointsEnabled}
            onChange={(e) => setPointsEnabled(e.target.checked)}
          />
          적립금 적립 사용
        </label>
        {/* 적립률은 포장/배달 주문별로 분리해 설정한다(개선). */}
        <div className="flex flex-wrap gap-3">
          <label className="flex w-40 flex-col gap-1 text-sm">
            <span className="text-ink-600">포장 적립률 (%)</span>
            <input
              type="number"
              min={0}
              max={100}
              value={pointsEarnPercent}
              onChange={(e) => setPointsEarnPercent(Number.parseInt(e.target.value, 10) || 0)}
              disabled={!pointsEnabled}
              className="rounded-md border border-ink-300 px-2 py-1 disabled:bg-ink-100"
            />
          </label>
          <label className="flex w-40 flex-col gap-1 text-sm">
            <span className="text-ink-600">배달 적립률 (%)</span>
            <input
              type="number"
              min={0}
              max={100}
              value={pointsEarnPercentDelivery}
              onChange={(e) =>
                setPointsEarnPercentDelivery(Number.parseInt(e.target.value, 10) || 0)
              }
              disabled={!pointsEnabled}
              className="rounded-md border border-ink-300 px-2 py-1 disabled:bg-ink-100"
            />
          </label>
        </div>

        {/* 적립금 사용 정책(개선항목 16) */}
        <p className="mb-2 mt-4 text-xs text-ink-600">
          적립금 사용 조건입니다. 예: 최소 사용 1000, 사용 단위 500 이면 1000원 이상부터 500원
          단위(1000, 1500, 2000...)로만 사용할 수 있습니다.
        </p>
        <div className="grid max-w-md grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-600">최소 사용 (원)</span>
            <input
              type="number"
              min={0}
              step={100}
              value={pointsMinUseKrw}
              onChange={(e) => setPointsMinUseKrw(Number.parseInt(e.target.value, 10) || 0)}
              disabled={!pointsEnabled}
              className="rounded-md border border-ink-300 px-2 py-1 disabled:bg-ink-100"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-600">사용 단위 (원)</span>
            {/* min 을 step 의 배수 기준(0)으로 둬야 500·1000 같은 값이 브라우저 step 검증을 통과한다.
                과거 min=1, step=100 이면 유효값이 1·101·201...501 로 어긋나 500/1000 입력이 막혔다(개선항목 12).
                0 입력 시에는 저장 단계에서 1 이상으로 정규화한다. */}
            <input
              type="number"
              min={0}
              step={100}
              value={pointsUseUnitKrw}
              onChange={(e) => setPointsUseUnitKrw(Number.parseInt(e.target.value, 10) || 0)}
              disabled={!pointsEnabled}
              className="rounded-md border border-ink-300 px-2 py-1 disabled:bg-ink-100"
            />
          </label>
        </div>
      </details>

      {/* 칭찬하고 할인받기(개선 5~8) */}
      <details className="rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          칭찬하고 할인받기
        </summary>
        <p className="mb-3 text-xs text-ink-600">
          손님이 주문 시 칭찬 1개를 고르면, 칭찬 할인 범위 안에서 무작위 금액을 할인해 줍니다(금액이
          클수록 확률이 낮습니다). 고른 칭찬/할인은 주문이 종결될 때까지 고정됩니다.
        </p>
        <label className="mb-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={complimentEnabled}
            onChange={(e) => setComplimentEnabled(e.target.checked)}
          />
          칭찬하고 할인받기 사용
        </label>
        {complimentEnabled ? (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-600">포장 주문 칭찬 개수</span>
                <input
                  type="number"
                  min={0}
                  max={20}
                  value={complimentPickupCount}
                  onChange={(e) =>
                    setComplimentPickupCount(Number.parseInt(e.target.value, 10) || 0)
                  }
                  className="rounded-md border border-ink-300 px-2 py-1 text-sm tabular-nums"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-600">배달 주문 칭찬 개수</span>
                <input
                  type="number"
                  min={0}
                  max={20}
                  value={complimentDeliveryCount}
                  onChange={(e) =>
                    setComplimentDeliveryCount(Number.parseInt(e.target.value, 10) || 0)
                  }
                  className="rounded-md border border-ink-300 px-2 py-1 text-sm tabular-nums"
                />
              </label>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-600">칭찬 할인 최소 (원)</span>
                {/* step=10, min=0 으로 둬 10/20/100/200 등 10원 단위 금액이 브라우저 step 검증을 통과하게 한다. */}
                <input
                  type="number"
                  min={0}
                  step={10}
                  value={complimentDiscountMinKrw}
                  onChange={(e) =>
                    setComplimentDiscountMinKrw(Number.parseInt(e.target.value, 10) || 0)
                  }
                  className="rounded-md border border-ink-300 px-2 py-1 text-sm tabular-nums"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-600">칭찬 할인 최대 (원)</span>
                <input
                  type="number"
                  min={0}
                  step={10}
                  value={complimentDiscountMaxKrw}
                  onChange={(e) =>
                    setComplimentDiscountMaxKrw(Number.parseInt(e.target.value, 10) || 0)
                  }
                  className="rounded-md border border-ink-300 px-2 py-1 text-sm tabular-nums"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-600">할인 단위 (원)</span>
                {/* 과거 min=1, step=100 이면 유효값이 1·101·201...로 어긋나 100/200 입력이 막혔다(개선 요청).
                    min=0, step=10 으로 두면 10·20·...·100·200·... 모두 통과한다. 0 은 저장 시 1 이상으로 정규화. */}
                <input
                  type="number"
                  min={0}
                  step={10}
                  value={complimentDiscountUnitKrw}
                  onChange={(e) =>
                    setComplimentDiscountUnitKrw(Number.parseInt(e.target.value, 10) || 0)
                  }
                  className="rounded-md border border-ink-300 px-2 py-1 text-sm tabular-nums"
                />
              </label>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setComplimentModalOpen(true)}
                className="h-9 rounded-md border border-ink-400 px-3 text-sm font-semibold text-ink-700 hover:bg-ink-100"
              >
                칭찬목록 등록
              </button>
              <span className="text-xs text-ink-600">현재 {complimentList.length}개 등록됨</span>
            </div>
          </div>
        ) : null}
      </details>

      {/* 회원 등급 정책 */}
      <details className="rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          회원 등급
        </summary>
        <p className="mb-3 text-xs text-ink-600">
          직전 N개월(현재 달 제외)의 완료 주문 합계로 등급이 자동 부여됩니다. 매월 1일 00시에
          갱신됩니다. 예) 산정 기간 3개월이면 6월 등급은 3/1~5/31 완료 실적 기준.
        </p>
        <label className="mb-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={tierEnabled}
            onChange={(e) => setTierEnabled(e.target.checked)}
          />
          회원 등급 사용
        </label>
        <label className="mb-3 flex max-w-xs flex-col gap-1 text-sm">
          <span className="text-ink-600">산정 기간 (개월)</span>
          <input
            type="number"
            min={1}
            max={24}
            value={tierWindowMonths}
            onChange={(e) => setTierWindowMonths(Number.parseInt(e.target.value, 10) || 3)}
            disabled={!tierEnabled}
            className="rounded-md border border-ink-300 px-2 py-1 disabled:bg-ink-100"
          />
        </label>

        <div className="flex flex-col gap-2">
          {tiers.length === 0 ? (
            <p className="text-xs text-ink-600">
              등급이 없습니다. 아래 버튼으로 추가하세요 (예: 단골 100,000원).
            </p>
          ) : (
            tiers.map((t, idx) => (
              <div key={t.key} className="flex flex-col gap-2 rounded-md border border-ink-200 p-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={t.name}
                    onChange={(e) =>
                      setTiers((arr) =>
                        arr.map((it, i) => (i === idx ? { ...it, name: e.target.value } : it)),
                      )
                    }
                    maxLength={20}
                    placeholder="등급 이름 (예: 단골)"
                    disabled={!tierEnabled}
                    className="h-9 min-w-0 flex-1 rounded-md border border-ink-300 px-2 text-sm disabled:bg-ink-100"
                  />
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      step={1000}
                      value={t.minSpendKrw}
                      onChange={(e) =>
                        setTiers((arr) =>
                          arr.map((it, i) =>
                            i === idx
                              ? { ...it, minSpendKrw: Number.parseInt(e.target.value, 10) || 0 }
                              : it,
                          ),
                        )
                      }
                      disabled={!tierEnabled}
                      className="h-9 w-32 rounded-md border border-ink-300 px-2 text-sm tabular-nums disabled:bg-ink-100"
                    />
                    <span className="text-xs text-ink-600">원 이상</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTiers((arr) => arr.filter((_, i) => i !== idx))}
                    disabled={!tierEnabled}
                    className="h-9 rounded-md border border-ink-300 px-2 text-xs text-ink-600 hover:bg-ink-100 disabled:opacity-50"
                  >
                    삭제
                  </button>
                </div>
                {/* 이 등급 회원에게 매월 발행할 쿠폰팩 */}
                <div className="rounded-md bg-ink-50 p-2">
                  <p className="mb-1 text-xs font-medium text-ink-600">월간 쿠폰팩</p>
                  <CouponSpecListEditor
                    specs={t.coupons}
                    onChange={(next) =>
                      setTiers((arr) =>
                        arr.map((it, i) => (i === idx ? { ...it, coupons: next } : it)),
                      )
                    }
                    disabled={!tierEnabled}
                  />
                </div>
              </div>
            ))
          )}
          <button
            type="button"
            onClick={() =>
              setTiers((arr) => [
                ...arr,
                { key: `t${Date.now().toString(36)}`, name: "", minSpendKrw: 0, coupons: [] },
              ])
            }
            disabled={!tierEnabled}
            className="h-9 self-start rounded-md border border-ink-400 px-3 text-sm font-semibold text-ink-700 hover:bg-ink-100 disabled:opacity-50"
          >
            + 등급 추가
          </button>
        </div>
      </details>

      {/* 가입 축하 쿠폰 */}
      <details className="rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          가입 축하 쿠폰
        </summary>
        <p className="mb-3 text-xs text-ink-600">
          신규 회원 가입 시 자동 발급할 쿠폰입니다. 유형/혜택/이름/유효기간/장수를 지정하세요.
        </p>
        <CouponSpecListEditor specs={signupCoupons} onChange={setSignupCoupons} />
      </details>

      {/* 1:1 문의 연결 */}
      <details className="rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          1:1 문의 연결
        </summary>
        <p className="mb-3 text-xs text-ink-600">
          사용자 고객센터 화면에 노출됩니다. 비워두면 해당 버튼이 숨겨집니다.
        </p>
        <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex min-w-0 flex-col gap-1 text-sm">
            <span className="text-ink-600">전화번호</span>
            <input
              type="text"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              maxLength={20}
              placeholder="예: 010-1234-5678"
              className="h-10 rounded-md border border-ink-300 px-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-600">카카오톡 채널 URL</span>
            <input
              type="text"
              value={kakaoUrl}
              onChange={(e) => setKakaoUrl(e.target.value)}
              maxLength={300}
              placeholder="예: https://pf.kakao.com/_xxxxx"
              className="h-10 rounded-md border border-ink-300 px-2 text-sm"
            />
          </label>
        </div>
      </details>

      {/* 앱 첫 화면(스플래시) 이미지 */}
      <details className="rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          앱 첫 화면(스플래시)
        </summary>
        <p className="mb-3 text-xs text-ink-600">
          사용자 앱을 처음 열 때 잠깐 표시되는 로딩 화면 이미지입니다. 설정하지 않으면 기본 브랜드
          화면이 표시됩니다. 세로형 전체 화면 이미지를 권장합니다.
        </p>
        <ImageUploader
          kind="product-detail"
          value={splashImageKey}
          onChange={setSplashImageKey}
          previewClassName="h-48 w-28"
        />
      </details>

      {/* 배달 정책 */}
      <details className="rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          배달 정책
        </summary>
        <label className="mb-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={deliveryEnabled}
            onChange={(e) => setDeliveryEnabled(e.target.checked)}
          />
          배달 주문 허용
        </label>
        <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex min-w-0 flex-col gap-1 text-sm">
            <span className="text-ink-600">최소 배달 주문 금액 (원)</span>
            <input
              type="number"
              min={0}
              value={minOrderKrw}
              onChange={(e) => setMinOrderKrw(Number.parseInt(e.target.value, 10) || 0)}
              disabled={!deliveryEnabled}
              className="w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 py-1 tabular-nums disabled:bg-ink-100"
            />
          </label>
          <label className="flex min-w-0 flex-col gap-1 text-sm">
            <span className="text-ink-600">배달비 (원)</span>
            <input
              type="number"
              min={0}
              value={feeKrw}
              onChange={(e) => setFeeKrw(Number.parseInt(e.target.value, 10) || 0)}
              disabled={!deliveryEnabled}
              className="w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 py-1 tabular-nums disabled:bg-ink-100"
            />
          </label>
        </div>

        {/* 다음날 배달 전용. 켜면 배달 주문은 시간 선택 없이 "보냉 포장 후 다음날 오전 배송"으로 고정된다.
            이때 배달 주문 가능 시간은 의미가 없어 아래 입력이 비활성된다(최소금액/지역키워드는 그대로). */}
        <label className="mt-4 flex min-w-0 items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={nextDayDeliveryOnly}
            onChange={(e) => setNextDayDeliveryOnly(e.target.checked)}
            disabled={!deliveryEnabled}
          />
          <span className="min-w-0 break-words">
            다음날 배달 주문만 (보냉 포장 후 다음날 오전 배송)
          </span>
        </label>

        {/* 배달 주문 가능 시간(개선 2). 설정 시 이 시간대에만 배달 주문/예약 배달이 가능. 비우면 제한 없음. */}
        <div className="mt-4">
          <p className="mb-1 text-sm font-medium text-ink-700">배달 주문 가능 시간 (선택)</p>
          <p className="mb-2 text-xs text-ink-600">
            {nextDayDeliveryOnly
              ? "다음날 배달 전용 모드에서는 시간 개념이 없어 사용하지 않습니다."
              : "설정하면 이 시간대에만 배달 주문과 예약 배달이 가능합니다(포장은 영향 없음). 비워두면 제한이 없습니다."}
          </p>
          <div className="grid min-w-0 grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
            <label className="flex min-w-0 flex-col gap-1 text-xs text-ink-600">
              <span className="sm:sr-only">시작 시각</span>
              <input
                type="time"
                value={deliveryWindowOpen}
                onChange={(e) => setDeliveryWindowOpen(e.target.value)}
                disabled={!deliveryEnabled || nextDayDeliveryOnly}
                className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900 disabled:bg-ink-100"
              />
            </label>
            <span className="hidden text-ink-600 sm:inline">~</span>
            <label className="flex min-w-0 flex-col gap-1 text-xs text-ink-600">
              <span className="sm:sr-only">마감 시각</span>
              <input
                type="time"
                value={deliveryWindowClose}
                onChange={(e) => setDeliveryWindowClose(e.target.value)}
                disabled={!deliveryEnabled || nextDayDeliveryOnly}
                className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900 disabled:bg-ink-100"
              />
            </label>
            <button
              type="button"
              onClick={() => {
                setDeliveryWindowOpen("");
                setDeliveryWindowClose("");
              }}
              disabled={!deliveryEnabled || nextDayDeliveryOnly}
              className="justify-self-end rounded-md border border-ink-300 px-3 py-1 text-xs text-ink-700 hover:bg-ink-100 disabled:opacity-50 sm:col-span-3"
            >
              비우기
            </button>
          </div>
        </div>

        <div className="mt-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-600">배달 가능 지역 키워드</span>
            <span className="text-xs text-ink-600">
              손님 주소에 키워드 중 하나라도 포함되면 배달 가능으로 판정합니다. 비워두면 모든 주소를
              허용합니다.
            </span>
          </label>
          <div className="mt-2 flex min-w-0 items-center gap-2">
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addKeyword();
                }
              }}
              placeholder="예: 강남구 도곡동"
              disabled={!deliveryEnabled}
              className="min-w-0 flex-1 rounded-md border border-ink-300 px-2 py-1 text-sm disabled:bg-ink-100"
            />
            <button
              type="button"
              onClick={addKeyword}
              disabled={!deliveryEnabled}
              className="shrink-0 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:bg-ink-300"
            >
              추가
            </button>
          </div>
          <ul className="mt-2 flex flex-wrap gap-2">
            {keywords.length === 0 ? (
              <li className="text-xs text-ink-600">등록된 키워드가 없습니다 (모든 주소 허용).</li>
            ) : (
              keywords.map((k) => (
                <li
                  key={k}
                  className="flex items-center gap-1 rounded-full border border-ink-300 bg-ink-50 px-3 py-1 text-sm"
                >
                  <span>{k}</span>
                  <button
                    type="button"
                    onClick={() => setKeywords(keywords.filter((x) => x !== k))}
                    className="rounded-full px-1 text-xs text-ink-600 hover:bg-ink-200"
                    aria-label="키워드 삭제"
                  >
                    x
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      </details>

      {/* 안내 문구 */}
      <details className="rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          사용자 안내 문구 (선택)
        </summary>
        <p className="mb-3 text-xs text-ink-600">
          사용자 앱 메인/주문 화면 하단에 노출됩니다. 한 줄로 간결하게 작성하세요.
        </p>
        <textarea
          value={noticeText}
          onChange={(e) => setNoticeText(e.target.value)}
          rows={3}
          maxLength={2000}
          className="w-full rounded-md border border-ink-300 px-2 py-1 text-sm"
          placeholder="예: 명절 연휴는 주문 마감이 빨라질 수 있습니다."
        />
      </details>

      {/* 칭찬목록 등록 모달(개선 7~8). 폼 안에 있으므로 모든 버튼은 type=button 으로 제출을 막는다. */}
      {complimentModalOpen ? (
        <ComplimentListModal
          list={complimentList}
          onChange={setComplimentList}
          onClose={() => setComplimentModalOpen(false)}
        />
      ) : null}

      {/* 결제 수단 안내(설정 항목 없음).
          결제수단은 상품의 "실중량 정산 대상" 체크로 자동 결정되므로 별도 토글을 두지 않는다. */}
      <details className="rounded-lg border border-ink-200 bg-white p-4 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-base font-bold text-ink-900 marker:text-ink-500">
          결제 수단 안내
        </summary>
        <ul className="list-disc space-y-1 pl-5 text-xs text-ink-600">
          <li>
            포장 주문: 일반 카드결제(주문 시 즉시) 또는 수령 시 현장 결제 중 손님이 선택합니다.
          </li>
          <li>배달 주문: 일반 카드결제 또는 준비 완료 후 문자(SMS) 결제 중 손님이 선택합니다.</li>
          <li>
            상품에 <span className="font-semibold">실중량 정산 대상</span>을 체크하면, 그 상품이
            담긴 주문은 금액이 나중에 확정되므로 카드 즉시결제가 막히고 현장/문자 결제만 가능합니다.
          </li>
          <li>
            등록 카드(자동결제) 기능은 카드사 정기결제 심사 대상이 아니어서 제공하지 않습니다.
          </li>
        </ul>
      </details>

      {error ? (
        <div className="rounded-md border border-danger-600 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="rounded-md border border-success-600 bg-success-50 px-3 py-2 text-sm text-success-700">
          {success}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-2 flex justify-end gap-2 border-t border-ink-200 bg-white/95 px-2 py-3 backdrop-blur">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:bg-ink-400"
        >
          {mutation.isPending ? "저장 중..." : "저장"}
        </button>
      </div>
    </form>
  );
}

// 칭찬목록 등록 모달(개선 7~8). 칭찬 리스트 + 칭찬 추가(하나씩) + 삭제.
function ComplimentListModal({
  list,
  onChange,
  onClose,
}: {
  list: string[];
  onChange: (next: string[]) => void;
  onClose: () => void;
}) {
  const [input, setInput] = useState("");

  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (list.includes(v)) {
      setInput("");
      return;
    }
    onChange([...list, v]);
    setInput("");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="compliment-modal-title"
        className="flex max-h-[85vh] w-full max-w-md flex-col gap-3 overflow-y-auto rounded-lg bg-surface p-5 shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="compliment-modal-title" className="text-lg font-bold text-ink-900">
          칭찬목록
        </h2>
        <p className="text-xs text-ink-600">
          손님이 주문 시 무작위로 보게 될 칭찬 문구입니다. 예: 고기가 맛있어요, 사장님 잘생겼어요.
        </p>

        {/* 칭찬 추가 */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
            maxLength={50}
            placeholder="칭찬 문구 입력"
            className="h-9 min-w-0 flex-1 rounded-md border border-ink-300 px-2 text-sm"
          />
          <button
            type="button"
            onClick={add}
            className="h-9 shrink-0 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700"
          >
            칭찬 추가
          </button>
        </div>

        {/* 칭찬 리스트 */}
        <ul className="flex flex-col gap-1">
          {list.length === 0 ? (
            <li className="rounded-md border border-dashed border-ink-300 p-4 text-center text-xs text-ink-600">
              등록된 칭찬이 없습니다.
            </li>
          ) : (
            list.map((c, idx) => (
              <li
                key={`${c}-${idx}`}
                className="flex items-center justify-between gap-2 rounded-md border border-ink-200 bg-white px-3 py-2 text-sm"
              >
                <span className="min-w-0 flex-1 truncate">{c}</span>
                <button
                  type="button"
                  onClick={() => onChange(list.filter((_, i) => i !== idx))}
                  className="shrink-0 rounded px-2 py-0.5 text-xs text-ink-600 hover:bg-ink-100"
                >
                  삭제
                </button>
              </li>
            ))
          )}
        </ul>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-touch items-center justify-center rounded-md border border-ink-300 bg-surface px-4 text-sm font-semibold text-ink-900 hover:bg-ink-50"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

// 쿠폰 발급 스펙 1건을 저장용으로 정규화한다(가입/등급 쿠폰팩 공용).
//  - free_delivery 는 value 0. 최대 할인금액은 정률에만 의미가 있어 그 외 유형은 제거.
//  - 최소 주문금액/최대 할인금액은 0/미설정이면 null(제한 없음)로 보낸다.
function normalizeCouponSpec(c: CouponSpec): CouponSpec {
  const minOrderKrw = c.minOrderKrw && c.minOrderKrw > 0 ? Math.floor(c.minOrderKrw) : null;
  const maxDiscountKrw =
    c.type === COUPON_TYPE.PERCENT && c.maxDiscountKrw && c.maxDiscountKrw > 0
      ? Math.floor(c.maxDiscountKrw)
      : null;
  return {
    type: c.type,
    value: c.type === COUPON_TYPE.FREE_DELIVERY ? 0 : Math.max(0, Math.floor(c.value || 0)),
    name: c.name.trim(),
    validDays: Math.max(1, Math.floor(c.validDays || 1)),
    count: Math.max(1, Math.floor(c.count || 1)),
    minOrderKrw,
    maxDiscountKrw,
    // 사용 조건(주문 방식). 'pickup'/'delivery' 외에는 null(전체).
    applicableOrderType:
      c.applicableOrderType === "pickup" || c.applicableOrderType === "delivery"
        ? c.applicableOrderType
        : null,
  };
}

// 쿠폰 발급 스펙 목록 편집기 (가입 쿠폰 / 등급 월간 쿠폰팩 공용).
// 각 행: 유형(정률/정액/배달비무료) + 혜택값 + 이름 + 유효기간(일) + 장수 + 삭제. 하단에 추가 버튼.
function CouponSpecListEditor({
  specs,
  onChange,
  disabled,
}: {
  specs: CouponSpec[];
  onChange: (next: CouponSpec[]) => void;
  disabled?: boolean;
}) {
  // 한 행만 부분 갱신.
  const patch = (idx: number, partial: Partial<CouponSpec>) =>
    onChange(specs.map((it, i) => (i === idx ? { ...it, ...partial } : it)));

  return (
    <div className="flex flex-col gap-2">
      {specs.length === 0 ? (
        <p className="text-xs text-ink-600">발급 쿠폰이 없습니다. 아래 버튼으로 추가하세요.</p>
      ) : (
        specs.map((s, idx) => (
          <div
            key={idx}
            className="flex flex-col gap-2 rounded-md border border-ink-200 bg-white p-2"
          >
            {/* 1줄: 유형/혜택값/이름(넓게)/유효기간/장수/삭제 */}
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={s.type}
                disabled={disabled}
                onChange={(e) => patch(idx, { type: e.target.value as CouponType })}
                className="h-9 rounded-md border border-ink-300 px-2 text-sm disabled:bg-ink-100"
              >
                <option value={COUPON_TYPE.PERCENT}>정률 할인</option>
                <option value={COUPON_TYPE.FIXED}>정액 할인</option>
                <option value={COUPON_TYPE.FREE_DELIVERY}>배달비 무료</option>
              </select>
              {s.type !== COUPON_TYPE.FREE_DELIVERY ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={0}
                    value={s.value}
                    disabled={disabled}
                    onChange={(e) =>
                      patch(idx, { value: Number.parseInt(e.target.value, 10) || 0 })
                    }
                    className="h-9 w-24 rounded-md border border-ink-300 px-2 text-sm tabular-nums disabled:bg-ink-100"
                  />
                  <span className="text-xs text-ink-600">
                    {s.type === COUPON_TYPE.PERCENT ? "%" : "원"}
                  </span>
                </div>
              ) : null}
              <input
                type="text"
                value={s.name}
                maxLength={30}
                placeholder="쿠폰 이름"
                disabled={disabled}
                onChange={(e) => patch(idx, { name: e.target.value })}
                className="h-9 min-w-[12rem] flex-1 rounded-md border border-ink-300 px-2 text-sm disabled:bg-ink-100"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={1}
                  value={s.validDays}
                  disabled={disabled}
                  onChange={(e) =>
                    patch(idx, { validDays: Number.parseInt(e.target.value, 10) || 1 })
                  }
                  className="h-9 w-16 rounded-md border border-ink-300 px-2 text-sm tabular-nums disabled:bg-ink-100"
                />
                <span className="text-xs text-ink-600">일</span>
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={1}
                  value={s.count}
                  disabled={disabled}
                  onChange={(e) => patch(idx, { count: Number.parseInt(e.target.value, 10) || 1 })}
                  className="h-9 w-14 rounded-md border border-ink-300 px-2 text-sm tabular-nums disabled:bg-ink-100"
                />
                <span className="text-xs text-ink-600">장</span>
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(specs.filter((_, i) => i !== idx))}
                className="h-9 rounded-md border border-ink-300 px-2 text-xs text-ink-600 hover:bg-ink-100 disabled:opacity-50"
              >
                삭제
              </button>
            </div>
            {/* 2줄 - 사용 조건: 주문 방식 제한 + 최소 주문금액(공통) + 최대 할인금액(정률만). 비워두면 제한 없음. */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="w-14 text-xs text-ink-600">사용조건</span>
                <select
                  value={s.applicableOrderType ?? ""}
                  disabled={disabled}
                  onChange={(e) =>
                    patch(idx, {
                      applicableOrderType:
                        e.target.value === "pickup" || e.target.value === "delivery"
                          ? e.target.value
                          : null,
                    })
                  }
                  className="h-9 rounded-md border border-ink-300 px-2 text-sm disabled:bg-ink-100"
                >
                  <option value="">전체</option>
                  <option value="pickup">포장 전용</option>
                  <option value="delivery">배달 전용</option>
                </select>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-14 text-xs text-ink-600">최소주문</span>
                <input
                  type="number"
                  min={0}
                  value={s.minOrderKrw ?? 0}
                  disabled={disabled}
                  onChange={(e) => {
                    const v = Number.parseInt(e.target.value, 10) || 0;
                    patch(idx, { minOrderKrw: v > 0 ? v : null });
                  }}
                  className="h-9 w-28 rounded-md border border-ink-300 px-2 text-sm tabular-nums disabled:bg-ink-100"
                />
                <span className="text-xs text-ink-600">원</span>
              </div>
              {s.type === COUPON_TYPE.PERCENT ? (
                <div className="flex items-center gap-1">
                  <span className="w-14 text-xs text-ink-600">최대할인</span>
                  <input
                    type="number"
                    min={0}
                    value={s.maxDiscountKrw ?? 0}
                    disabled={disabled}
                    onChange={(e) => {
                      const v = Number.parseInt(e.target.value, 10) || 0;
                      patch(idx, { maxDiscountKrw: v > 0 ? v : null });
                    }}
                    className="h-9 w-28 rounded-md border border-ink-300 px-2 text-sm tabular-nums disabled:bg-ink-100"
                  />
                  <span className="text-xs text-ink-600">원</span>
                </div>
              ) : null}
            </div>
          </div>
        ))
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() =>
          onChange([
            ...specs,
            { type: COUPON_TYPE.PERCENT, value: 5, name: "", validDays: 7, count: 1 },
          ])
        }
        className="h-9 self-start rounded-md border border-ink-400 px-3 text-sm font-semibold text-ink-700 hover:bg-ink-100 disabled:opacity-50"
      >
        + 쿠폰 추가
      </button>
    </div>
  );
}
